import { computed, Directive, effect, ElementRef, inject, Injector, input, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { fromEvent, merge, timer } from 'rxjs';
import { map, mergeMap, startWith, take, takeUntil } from 'rxjs/operators';
import { OverlayRef, OverlaysService, POSITION_TARGET, PositionTarget } from '../overlays';
import { TooltipComponent } from './tooltip.component';


export let tooltipLastId = 0;


@Directive({
    selector: '[tooltip]',
    host: {
        '[attr.aria-describedby]': 'tooltipId',
    }
})
export class TooltipDirective implements OnDestroy, PositionTarget {

    content = input.required<string | null | undefined>({ alias: 'tooltip' });

    placement = input<'top' | 'bottom' | 'left' | 'right'>('top', { alias: 'tooltipPlacement' });

    disabled = input(false, { alias: 'tooltipDisabled' });

    show = input(false, { alias: 'tooltipShow' });

    userEvents = input(true, { alias: 'tooltipUserEvents' });

    tooltipId = `tooltip${++tooltipLastId}`;

    private tooltip: OverlayRef | null = null;

    private elementRef = inject(ElementRef);

    private overlaysService = inject(OverlaysService);

    private injector = inject(Injector);

    constructor() {
        const element = this.elementRef.nativeElement;

        const userLeave = merge(fromEvent(element, 'mouseleave'), fromEvent(element, 'focusout'));
        const userEnter = merge(fromEvent(element, 'mouseenter'), fromEvent(element, 'focusin')).pipe(
            mergeMap(() => timer(800).pipe(takeUntil(userLeave)))
        );

        const userShow = toSignal(
            userEnter.pipe(mergeMap(() => userLeave.pipe(take(1), map(_ => false), startWith(true))), startWith(false))
        );

        const show = computed(() => (this.show() || (userShow() && this.userEvents())) && !this.disabled());

        effect(() => {
            if (show()) {
                this.showTooltip();
            } else {
                this.hideTooltip();
            }
        });

        effect(() => {
            if (this.content() && this.tooltip) {
                this.tooltip.componentRef.setInput('content', this.content());
                this.tooltip.componentRef.changeDetectorRef.detectChanges();
                this.tooltip.componentRef.instance.calcPosition();
            }
        });
    }

    getBoundingClientRect() {
        return this.elementRef.nativeElement.getBoundingClientRect();
    }

    private showTooltip() {
        if (!this.elementRef.nativeElement.parentElement) {
            return;
        }
        if (this.tooltip || this.disabled()) {
            return;
        }
        const injector = Injector.create({
            providers: [{ provide: POSITION_TARGET, useValue: this }],
            parent: this.injector,
        });
        this.tooltip = this.overlaysService.createOverlay(TooltipComponent, {
            inputs: {
                content: this.content(),
                placement: this.placement(),
                tooltipId: this.tooltipId,
            },
            parentInjector: injector,
        });
        this.tooltip.componentRef.onDestroy(() => {
            this.tooltip = null;
        });
    }

    private hideTooltip() {
        if (this.tooltip) {
            this.tooltip.dismiss('hide');
        }
    }

    ngOnDestroy() {
        this.hideTooltip();
    }
}
